import { useEffect, useState } from "react";
import { Bell, BellOff, Send, AlertCircle, Check } from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  isPushSupported,
  pushPermission,
  getCurrentSubscription,
  subscribePush,
  unsubscribePush,
  sendTestPush,
} from "../lib/push";
import { toast } from "sonner";

export default function NotificationsPage() {
  const [supported] = useState(isPushSupported());
  const [permission, setPermission] = useState(pushPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const sub = await getCurrentSubscription();
      setSubscribed(!!sub);
    })();
  }, []);

  const onSubscribe = async () => {
    setLoading(true);
    try {
      await subscribePush();
      setPermission(pushPermission());
      setSubscribed(true);
      toast.success("Notifiche attivate");
    } catch (err) {
      toast.error(err.message || "Errore");
    } finally {
      setLoading(false);
    }
  };

  const onUnsubscribe = async () => {
    setLoading(true);
    try {
      await unsubscribePush();
      setSubscribed(false);
      toast.success("Notifiche disattivate");
    } catch (err) {
      toast.error(err.message || "Errore");
    } finally {
      setLoading(false);
    }
  };

  const onTest = async () => {
    try {
      const res = await sendTestPush();
      if (res.data?.sent > 0) toast.success("Notifica di prova inviata");
      else toast.warning("Notifica inviata ma nessun dispositivo è iscritto");
    } catch {
      toast.error("Errore invio notifica");
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="notifications-page">
      <div>
        <div className="label-eyebrow">Notifiche</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">
          Notifiche Push
        </h1>
        <p className="text-slate-500 mt-1">
          Ricevi notifiche istantanee per convocazioni, analisi partita e promemoria allenamenti — anche quando l'app è chiusa.
        </p>
      </div>

      {!supported && (
        <Card className="p-5 border-amber-200 bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={20} />
            <div>
              <div className="font-semibold text-amber-900">Browser non compatibile</div>
              <p className="text-sm text-amber-800 mt-1">
                Le notifiche push non sono supportate da questo browser. Usa Chrome, Edge, Firefox o Safari iOS 16.4+ installato come app.
              </p>
            </div>
          </div>
        </Card>
      )}

      {supported && permission === "denied" && (
        <Card className="p-5 border-red-200 bg-red-50">
          <div className="flex items-start gap-3">
            <BellOff className="text-red-600 shrink-0 mt-0.5" size={20} />
            <div>
              <div className="font-semibold text-red-900">Notifiche bloccate</div>
              <p className="text-sm text-red-800 mt-1">
                Hai negato il permesso. Vai nelle impostazioni del browser/sistema, sblocca le notifiche per questo sito e ricarica la pagina.
              </p>
            </div>
          </div>
        </Card>
      )}

      {supported && permission !== "denied" && (
        <Card className="p-6 border-slate-200">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div
                className={`h-12 w-12 rounded-2xl flex items-center justify-center ${
                  subscribed
                    ? "bg-emerald-50 text-emerald-600"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {subscribed ? <Bell size={22} /> : <BellOff size={22} />}
              </div>
              <div>
                <div className="font-semibold">
                  {subscribed ? "Notifiche attive su questo dispositivo" : "Notifiche disattivate"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Permesso browser: <span className="font-semibold">{permission}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {subscribed ? (
                <>
                  <Button
                    variant="outline"
                    onClick={onTest}
                    disabled={loading}
                    data-testid="push-test-btn"
                  >
                    <Send size={14} className="mr-1" /> Invia prova
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onUnsubscribe}
                    disabled={loading}
                    data-testid="push-unsubscribe-btn"
                  >
                    <BellOff size={14} className="mr-1" /> Disattiva
                  </Button>
                </>
              ) : (
                <Button
                  onClick={onSubscribe}
                  disabled={loading}
                  className="bg-sky-500 hover:bg-sky-600"
                  data-testid="push-subscribe-btn"
                >
                  <Bell size={14} className="mr-1" /> Attiva notifiche
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 border-slate-200 bg-slate-50/50">
        <div className="label-eyebrow mb-3">Eventi che riceverai</div>
        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />
            <span><strong>Convocazione pubblicata</strong> — quando il Mister pubblica la lista convocati di una partita</span>
          </li>
          <li className="flex items-start gap-2">
            <Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />
            <span><strong>Analisi post-match</strong> — quando viene pubblicato il report di una partita giocata</span>
          </li>
          <li className="flex items-start gap-2">
            <Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />
            <span><strong>Promemoria allenamento</strong> — 2 ore prima dell'inizio della seduta</span>
          </li>
        </ul>
        <p className="text-xs text-slate-500 mt-4">
          Le notifiche sono inviate solo al ruolo Genitore per i giocatori effettivamente convocati. Funziona anche con l'app chiusa o lo schermo bloccato (su iOS richiede installazione PWA dalla schermata Home).
        </p>
      </Card>
    </div>
  );
}
